import { useMemo, useState } from 'react';

import { useClusterJobRunsInfinite } from '@/Pages/ClusterJob/hooks/useSessionClusterJobs';
import { TableSkeleton } from '@/Pages/Home/ResourceTable/TableSkeleton';
import { RUNS_TABLE_COLUMNS } from '@/components/RunsTable/utils/constants';
import { SplitViewPane } from '@/components/SplitViewPane';
import { GroupedTabs, Text } from '@/design-system';
import { GridTimeModel } from '@/hooks/useDataGridState';
import { RunSchema } from '@/types/schema';
import { xCount } from '@/utils/stringUtils';

import { TreeNode } from '../types';
import { CategoryTraces } from './CategoryTraces';
import { LeafRunsDisplay, ParentRunsDisplay } from './RunsDisplay';

enum CategoryDetailsPaneTab {
  Summaries = 'summaries',
  Traces = 'traces',
}

const COLUMN_VISIBILITY_MODEL_BY_TAB = {
  [CategoryDetailsPaneTab.Summaries]: {
    ...Object.fromEntries(
      Object.values(RUNS_TABLE_COLUMNS).map((key) => [key, false])
    ),
    select: true,
    status: true,
    summary: true,
  },
  [CategoryDetailsPaneTab.Traces]: {
    summary: false,
  },
};

const PAGE_SIZE = 50;

const CategoryTracesWrapper = ({
  runs,
  hasInitialLoad,
  hasMoreRuns,
  loadMoreRuns,
  clusterJobId,
  jobTimeRange,
  category,
  tab,
  isLoading,
  isValidating,
  error,
  attributes,
}: {
  runs: RunSchema[];
  hasInitialLoad: boolean;
  hasMoreRuns: boolean;
  loadMoreRuns: () => void;
  clusterJobId: string;
  jobTimeRange: GridTimeModel;
  category?: TreeNode;
  tab: CategoryDetailsPaneTab;
  isLoading: boolean;
  isValidating: boolean;
  error: unknown;
  attributes: string[];
}) => {
  const clusterId = category?.id;

  const columnVisibility = useMemo(() => {
    return COLUMN_VISIBILITY_MODEL_BY_TAB[tab];
  }, [tab]);

  if (runs.length === 0) {
    if (hasInitialLoad) {
      return (
        <div className="py-8 text-center">
          <div className="mb-2 text-tertiary">
            {clusterId ? 'No runs found in this cluster' : 'No runs found'}
          </div>
        </div>
      );
    } else {
      return (
        <TableSkeleton
          rows={20}
          rowHeight={48}
          className="rounded-none border-none"
        />
      );
    }
  }

  return (
    <CategoryTraces
      runs={runs}
      columnVisibility={columnVisibility}
      clusterJobId={clusterJobId}
      jobTimeRange={jobTimeRange}
      loadMoreRuns={loadMoreRuns}
      hasMoreRuns={hasMoreRuns}
      isValidating={isValidating}
      isLoading={isLoading}
      error={error}
      attributes={attributes}
    />
  );
};

export const CategoryDetailsPane = ({
  onClose,
  sessionId,
  clusterJobId,
  jobTimeRange,
  attributes,
  category,
}: {
  onClose: () => void;
  sessionId: string;
  clusterJobId: string;
  jobTimeRange: GridTimeModel;
  attributes: string[];
  category?: TreeNode;
}) => {
  const [categoryRunsPaneTab, setCategoryRunsPaneTab] =
    useState<CategoryDetailsPaneTab>(CategoryDetailsPaneTab.Summaries);

  const onClosePane = () => {
    onClose();
    setCategoryRunsPaneTab(CategoryDetailsPaneTab.Summaries);
  };

  const { data, size, setSize, isValidating, isLoading, error } =
    useClusterJobRunsInfinite(sessionId, clusterJobId, category?.id, {
      limit: PAGE_SIZE,
    });

  const allRuns = useMemo(
    () => data?.flatMap((page) => page.runs || []) || [],
    [data]
  );

  const hasInitialLoad = !!data;

  const hasMoreRuns = useMemo(() => {
    if (!allRuns || allRuns.length === 0) return false;
    if (category?.num_runs && allRuns.length < category.num_runs) return true;
    return false;
  }, [allRuns, category]);

  const loadMoreRuns = () => {
    if (isValidating || !hasMoreRuns) return;
    setSize(size + 1);
  };

  return (
    <SplitViewPane
      open={!!category}
      title={
        <Text variant="sm" weight="semibold">
          {category?.name}
        </Text>
      }
      onClose={onClosePane}
      hideArrows
    >
      {category && (
        <>
          <div className="flex justify-between gap-2 p-4">
            <div className="flex flex-col gap-2">
              <Text variant="h2" weight="semibold">
                {`${category.name} (${xCount('run', category.num_runs || 0)})`}
              </Text>
              <Text variant="sm" color="tertiary">
                {category.description}
              </Text>
            </div>
            <div className="flex h-fit flex-row gap-2">
              {category.isLeaf && category.depth > 0 ? (
                <div className="my-auto rounded-sm border border-secondary py-1 pl-1 pr-2">
                  <LeafRunsDisplay node={category} />
                </div>
              ) : (
                <ParentRunsDisplay node={category} />
              )}
              <GroupedTabs<CategoryDetailsPaneTab>
                value={categoryRunsPaneTab}
                onChange={(value) => setCategoryRunsPaneTab(value)}
                options={[
                  {
                    display: 'Summaries',
                    value: CategoryDetailsPaneTab.Summaries,
                  },
                  {
                    display: 'Traces',
                    value: CategoryDetailsPaneTab.Traces,
                  },
                ]}
                size="sm"
              />
            </div>
          </div>
          <CategoryTracesWrapper
            runs={allRuns}
            hasInitialLoad={hasInitialLoad}
            hasMoreRuns={hasMoreRuns}
            loadMoreRuns={loadMoreRuns}
            tab={categoryRunsPaneTab}
            clusterJobId={clusterJobId}
            jobTimeRange={jobTimeRange}
            category={category}
            isLoading={isLoading}
            isValidating={isValidating}
            error={error}
            attributes={attributes}
          />
        </>
      )}
    </SplitViewPane>
  );
};
